import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useLocation } from 'react-router';
import usePrevious from 'hooks/usePrevious';
import SubMenu from './SubMenu'; // eslint-disable-line
import MenuLink from './MenuLink';
import {
  setActiveTrail,
  toggleSubmenu,
  addToActiveTrail,
  toggleMobileMenu,
  setIsOpen,
} from '../../../store/ui/menu/actions';
import {
  getActiveDepth,
  getIsActive,
  isOpen as isOpenSelector,
  getIsMenuOpenMobile,
} from '../../../store/ui/menu/selectors';

const MenuItemWrapper = styled.li`
  display: flex;
  flex-direction: column;
`;

export default function MenuItem({ item, depth, ...otherProps }) {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const prevPathname = usePrevious(pathname);
  const activeDepth = useAppSelector(getActiveDepth);
  const isActive = useAppSelector(getIsActive(item));
  const isOpen = useAppSelector(isOpenSelector);
  const isOpenMobile = useAppSelector(getIsMenuOpenMobile);

  const handleMenuItemClick = useCallback(() => {
    // Do nothing if item is clicked twice
    if (isActive) {
      return;
    }
    // Toggle expandable submenu if necessary
    const closedButShouldBeOpen = depth === 0 && item.children && !isOpen;
    const openButShouldBeClosed = depth === 0 && !item.children && isOpen;
    if (closedButShouldBeOpen || openButShouldBeClosed) {
      dispatch(toggleSubmenu());
    }
    // Add menu item to active trail
    if (depth === 0) {
      dispatch(setActiveTrail([item.key]));
    } else {
      const replaceCurrent = activeDepth > depth;
      dispatch(addToActiveTrail(item.key, replaceCurrent));
    }
    if (isOpenMobile && !item.children) {
      dispatch(toggleMobileMenu());
    }
  }, [activeDepth, depth, dispatch, isActive, isOpen, isOpenMobile, item.children, item.key]);

  const autoExpand = useCallback(() => {
    if (pathname.startsWith(item.route)) {
      handleMenuItemClick();
      // If handleMenuItemClick is evaluated while menu items are still loading (e.g. space menu),
      // toggleSubmenu will not be called when it should as there are no children at that point.
      if (depth > 0 && !isOpen) {
        dispatch(setIsOpen(true));
      }
    }
  }, [handleMenuItemClick, item.route, pathname]); // eslint-disable-line

  useEffect(() => {
    autoExpand();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (pathname !== prevPathname) {
      autoExpand();
    }
  }, [autoExpand, pathname, prevPathname]);

  if (item.isHidden) {
    return <div />;
  }
  return (
    <MenuItemWrapper key={item.key} {...otherProps}>
      {!item.component ? (
        <MenuLink to={item.route} depth={depth} onClick={handleMenuItemClick} end={item.useExactRoute}>
          <span>
            {item.icon && item.icon} {item.name} {item.iconAfter && item.iconAfter}
          </span>
        </MenuLink>
      ) : (
        item.component
      )}

      {item.children && (
        <SubMenu
          name={item.name}
          items={item.children}
          depth={depth + 1}
          isActive={isActive}
          hasFilter={item.hasFilter}
          filterLabel={item.filterLabel}
        />
      )}
      {isActive && item.childComponent}
    </MenuItemWrapper>
  );
}
MenuItem.propTypes = {
  item: PropTypes.object.isRequired, // eslint-disable-line
  depth: PropTypes.number,
};
MenuItem.defaultProps = {
  depth: 0,
};
